import { useEffect, useState } from 'react'
import { api, type Vendor } from '../lib/api'
import { ErrorBox } from '../components/DataTable'
import { VendorExit } from '../components/VendorExit'
import { Skeleton } from '../components/ui'
import '../styles/exit.css'

// The exit sign-off workspace for one vendor, at #/exits/<vendorId>.
//
// # WHY A PAGE OF ITS OWN
//
// The exit lived in the vendor record's Exit section, a narrow column beside
// the section menu, where the track, the next-up bar, the chain, the evidence
// and the plan were squeezed until they read as basic. An exit is also its own
// piece of work with its own people: a CISO asked to sign comes for the exit,
// not for the vendor's profile. So it has the page's full width, under
// Offboarding, and every door into an exit (a card on the board, a row in
// Sign-offs, a Decide on Home, a notice in the bell, the vendor header) opens
// it here.
//
// The page reads the vendor only for its name and code. Everything about the
// exit is VendorExit's, which reads the chain, the checklist and the summary.

export function ExitWorkspace({ vendorId }: { vendorId: string }) {
  const [vendor, setVendor] = useState<Vendor | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    let live = true
    api
      .vendor(vendorId)
      .then((v) => {
        if (live) setVendor(v)
      })
      .catch((err) => {
        if (live) setError(err)
      })
    return () => {
      live = false
    }
  }, [vendorId])

  if (error != null) return <ErrorBox error={error} what="This vendor's offboarding" />
  if (!vendor)
    return (
      <div className="p1-page exit-page" aria-busy="true">
        <p className="exit-crumb">
          <a href="#/exits">Offboarding</a>
        </p>
        <Skeleton lines={2} height={22} width="40%" />
        <div className="panel exit-stage">
          <Skeleton lines={3} height={18} />
        </div>
      </div>
    )

  const name = vendor.displayName?.trim() || vendor.legalName
  return (
    <div className="p1-page exit-page">
      <VendorExit
        vendorId={vendorId}
        head={{ name, code: vendor.vendorCode }}
        onChanged={() => {
          api
            .vendor(vendorId)
            .then(setVendor)
            .catch(() => undefined)
        }}
      />
    </div>
  )
}
